import type { PaginationParams } from '@/shared/ui/paginator';
import { UiTableModule } from '@/ui/table/ui-table.module';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import type { primitive } from '@/utils/typing/primitive.type';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, type OnDestroy, type OnInit, Output } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject, combineLatest, type Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';
import type { BusinessIntelligenceMemberCode } from '../../../interfaces/BusinessIntelligence.interface';
import type { BusinessIntelligenceRepresentationComponentConfig } from '../../../interfaces/BusinessIntelligenceRepresentation.interface';
import { SharedUiPaginatorComponent } from '../../../libs/shared/ui/paginator/lib/shared-ui-paginator/shared-ui-paginator.component';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { BusinessIntelligenceRepresentationComponent } from '../business-intelligence-representation-component.class';

export interface BusinessIntelligenceTableComponentConfig extends BusinessIntelligenceRepresentationComponentConfig {
    orderedColumnMemberCodes: BusinessIntelligenceMemberCode[];
    isResultCountLimited: boolean;
}

const DEFAULT_INIT_PAGINATION = { page: 0, pageSize: 5 };
@UntilDestroy()
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-table.scss'],
    selector: 'business-intelligence-table',
    templateUrl: 'business-intelligence-table.html',
    imports: [UiTableModule, SharedUiPaginatorComponent, LoadingAndErrorComponent, AsyncPipe]
})
export class BusinessIntelligenceTableComponent
    extends BusinessIntelligenceRepresentationComponent<BusinessIntelligenceTableComponentConfig>
    implements OnInit, OnDestroy
{
    @Output() paginationRequested: EventEmitter<{ limit: number; pageCount: number } | null> = new EventEmitter();

    orderedColumnMemberCodes$!: Observable<BusinessIntelligenceMemberCode[]>;
    lines$!: Observable<Record<BusinessIntelligenceMemberCode, Observable<primitive>>[]>;
    isResultCountLimited$!: Observable<boolean>;
    paginationParams$: BehaviorSubject<PaginationParams> = new BehaviorSubject(DEFAULT_INIT_PAGINATION);
    totalElements?: number;

    formattingInformation: Record<BusinessIntelligenceMemberCode, { headerName: string; columnAlignment: string }> = {};

    ngOnInit(): void {
        this.isResultCountLimited$ = this._config$.pipe(map(config => config.isResultCountLimited));

        combineLatest([this.paginationParams$, this.isResultCountLimited$])
            .pipe(
                filter(([_, isResultCountLimited]) => !isResultCountLimited),
                untilDestroyed(this)
            )
            .subscribe(([paginatedParams, _]) => {
                this.paginationRequested.emit({ limit: paginatedParams.pageSize, pageCount: paginatedParams.page });
            });

        this.orderedColumnMemberCodes$ = combineLatest([this._config$, this.isResultCountLimited$]).pipe(
            tap(([_, isResultCountLimited]) => {
                if (!isResultCountLimited) {
                    this.paginationParams$.next(DEFAULT_INIT_PAGINATION);
                }
            }),
            map(([config, _]) => config.orderedColumnMemberCodes)
        );

        this.lines$ = this._representationData$.pipe(
            filter(isNotNullish),
            tap(representationData => {
                this.totalElements = representationData.elementCount;
            }),
            map(representationData =>
                representationData.data
                    .map(dataPoint => {
                        return { ...dataPoint.measures, ...dataPoint.dimensions };
                    })
                    .map((dataPoint: any) => {
                        Object.keys(dataPoint).forEach(memberCode => {
                            dataPoint[memberCode] = representationData.metadata[
                                memberCode
                            ].valuesFormatting.groupedText.formatAsGroupedText$(dataPoint[memberCode]);
                        });
                        return dataPoint;
                    })
            )
        );

        this._representationData$
            .pipe(
                filter(isNotNullish),
                map(representationData => representationData.metadata),
                distinctUntilChanged(),
                switchMap(representationDataMetadata =>
                    Object.keys(representationDataMetadata).map(memberCode =>
                        combineLatest([
                            of(memberCode as BusinessIntelligenceMemberCode),
                            representationDataMetadata[memberCode].getName$(),
                            representationDataMetadata[memberCode].valuesFormatting.groupedText.getRepeatedInformation$(),
                            representationDataMetadata[memberCode].valuesFormatting.groupedText.getAlignment$()
                        ])
                    )
                ),
                untilDestroyed(this)
            )
            .subscribe(formattingDataArray => {
                formattingDataArray.forEach(([memberCode, formattedName, repeatedInformation, columnAlignment]) => {
                    this.formattingInformation[memberCode] = {
                        headerName: repeatedInformation ? `${formattedName} (${repeatedInformation})` : formattedName,
                        columnAlignment
                    };
                });
            });
    }

    ngOnDestroy(): void {
        this.paginationRequested.emit(null);
    }

    setPagination(event: PaginationParams) {
        this.paginationParams$.next(event);
    }

    getExportMembers(config: BusinessIntelligenceTableComponentConfig): BusinessIntelligenceMemberCode[] {
        return config.orderedColumnMemberCodes;
    }
}
